import { seg, tween } from '../../../core/effect/motion';
import {
  add, canvasOf, fade, fill, place, playerColor,
} from './common';
import { PLATE, centerColumn } from './chainPlate';
import {
  appearAlpha, appearAt, centerOf, makeJoin, makePlate, placeJoin,
} from './chainColumn';

/**
 * **チェーンの処理が始まる**（Issue #568。指示書 `../direction/chain.md` の「chain_solving（#568）」）。
 * チェーンの列（`chained` と同じ並び・がたつき）から、処理するリンクの札を拡大して「処理が始まった」ことを示す。
 *
 * ```
 * 0     盤・背景を少し暗く（chained と同じ 30%）し、今残っているチェーンの列を中央に出す（処理済みの札は出さない）
 * 120   処理するリンクの札（列の一番下）が列から抜け出て中央へ 1.6 倍に拡大（傾きは戻る）。ほかの札とつなぎは少し暗く
 * 300   見得: 拡大した札の縁がプレイヤーの色の硬い線で締まる
 * 500   列と札が縦に潰れてはけ、暗さが戻る（このあと効果の中身のイベントがそれぞれの演出で続く）
 * 600   終わり（何も残らない）
 * ```
 *
 * **無効にされたリンク**（発動の無効 `link.negated` も効果の無効 `link.disabled` も。ユーザー 2026-09-28）: 札は鎖で縛られている
 * （鎖の感じだけ違う。`../fx/chainPlate.js::BINDS`）→ 拡大したところで**灰色になって沈む**（色が抜けて暗くなり、少し下がって消える）。
 * **札は割れない**（発動を無効にしても破壊しないカード・効果を無効にして破壊するカードがある。破壊は move の演出が見せる）
 *
 * 処理済みの札を出さない: サービスは解決したリンクを `chain_solved` で積まれている列から降ろすので、この瞬間の `chainLinks` に
 * 残っているのは「処理するリンク」と「まだ処理していないリンク（番号が小さい）」だけ。番号で絞った `links` と状況（`zoom` / `sink`）は
 * 分岐レイヤー（`editorPorts/duelSituations/chain_solving.js`）が payload に付ける（Issue #589）。
 * 演出は payload とその瞬間の盤面だけ。もやもやした光は使わない。
 */
export const D = 600;
export const PULL = 120;     // 抜け出始める
export const BIG = 300;      // 拡大し終わる（見得の始まり）
export const SINK = 330;     // 無効: 灰色になり始める
export const EXIT = 500;     // はけ始める
export const GONE = 590;     // 消え終わる
/** 盤・背景を暗くする濃さ（chained と同じ） */
export const DIM = 0.3;
/** 拡大の倍率（列の札に対する） */
export const ZOOM = 1.6;
/** ほかの札を暗くする明るさ */
const SHADE = 0.55;

/** 列が潰れる量（1 → 0） */
const squashAt = (t) => 1 - tween(t, EXIT, GONE, 'power3_in');

/** チェーンの処理の組み立て。`mode` = `'zoom'`（ふつう）/ `'sink'`（無効にされたリンク。灰色で沈む） */
export const chainSolvingSpec = (mode) => ({
  label: 'チェーンの処理',
  color: '#ffd21a',
  duration: () => D,
  /*
   * **無効にされたリンクは `chain_solved` が来ない**ので、ここでそのリンクの対象の照準の枠を消す（Issue #571）。
   * 札が灰色になって沈み始めるところで消す
   */
  clears: ({ payload }) => (mode === 'sink' && payload?.chain ? [{ mark: 'targetSight', prefix: `${payload.chain}:`, atMs: SINK }] : []),

  layers: [
    {
      id: 'column',
      label: 'チェーンの列（処理するリンクの札が抜け出て拡大。無効なら灰色で沈む）',
      space: 'screen',
      script: (ctx) => {
        const { THREE } = ctx;
        const canvas = canvasOf(ctx);
        const { w: W, h: H } = canvas;
        const { payload } = ctx;
        const root = new THREE.Group();
        const links = payload?.links ?? [];
        const n = links.length;
        if (!n) return { root };
        const col = centerColumn(n, canvas, links);
        const midY = (col[0].y + col[n - 1].y + col[n - 1].h) / 2;
        const target = { x: W / 2, y: H * 0.52 };

        const dim = add(root, place(fill(ctx, '#040406', W, H), W / 2, H / 2), 0);
        const joins = links.slice(1).map((_, i) => makeJoin(ctx, root, 9 + i * 0.01));
        const plates = links.map((l, i) => Object.assign(makePlate(ctx, l, 10 + i, root), { index: i, me: i === n - 1 }));
        const me = plates[n - 1];

        // 見得の縁（プレイヤーの色の硬い線 4 本）
        const color = playerColor(ctx, me.link.player);
        const edge = [0, 1, 2, 3].map(() => add(root, fill(ctx, color, 1, 1), 30));

        return {
          root,
          update: ({ localMs: t }) => {
            const shown = t >= 0 && t < GONE;
            const q = squashAt(t);
            fade(dim, DIM * seg(t, 0, 150) * (1 - seg(t, EXIT, GONE)));
            const shade = 1 - (1 - SHADE) * seg(t, PULL, PULL + 100);

            plates.forEach((pl) => {
              pl.refresh();
              const slot = centerOf(col[pl.index]);
              const k0 = appearAt(t, pl.index);
              let at = { x: slot.x, y: slot.y, s: slot.s * (0.9 + 0.1 * k0), rot: slot.rot };
              let alpha = appearAlpha(t, pl.index) * col[pl.index].fade;
              if (pl.me) {
                // 列から抜け出て中央へ拡大（傾きは戻る）
                const k = tween(t, PULL, BIG, 'power3_out');
                const big = slot.s * ZOOM * (1 + 0.015 * seg(t, BIG, EXIT));
                at = {
                  x: at.x + (target.x - at.x) * k, y: at.y + (target.y - at.y) * k, s: at.s + (big - at.s) * k, rot: at.rot * (1 - k),
                };
                alpha = appearAlpha(t, pl.index);
                if (mode === 'sink') {
                  // 灰色になって沈む（色が抜けて暗くなり、少し下がって消える）
                  const gk = tween(t, SINK, SINK + 90, 'ease_out');
                  at.y += 60 * tween(t, SINK + 40, EXIT, 'power2_in');
                  alpha *= 1 - seg(t, SINK + 100, EXIT);
                  fade(pl.grey, shown ? alpha * gk : 0);
                } else {
                  fade(pl.grey, 0);
                }
              } else {
                fade(pl.grey, 0);
                pl.mesh.material.color?.setScalar?.(shade);
              }
              const s = at.s;
              pl.at = { x: at.x, y: pl.me ? at.y : midY + (at.y - midY) * q, s, sy: s * q, rot: at.rot * q };
              place(pl.g, pl.at.x, pl.at.y, { sx: s, sy: Math.max(0.001, pl.at.sy), rot: pl.at.rot });
              fade(pl.g, shown ? alpha : 0);
              fade(pl.flash, 0);
            });

            // 鎖のつなぎ（抜け出す札へのつなぎは抜け出す間に消える）
            joins.forEach((m, i) => {
              const dy = placeJoin(m, plates[i].at, plates[i + 1].at);
              const toMe = i + 1 === n - 1;
              const a = appearAlpha(t, i + 1) * col[i].fade * (toMe ? 1 - seg(t, PULL, PULL + 80) : 1);
              m.material.color?.setScalar?.(shade);
              fade(m, shown && dy > 1 ? a : 0);
            });

            // 見得の縁: 拡大した札の外から締まる（無効のときは沈み始めるまで）
            const p = me.at;
            const ek = tween(t, BIG - 40, BIG + 80, 'expo_out');
            const k = 1.14 - 0.1 * ek;
            const hw = (PLATE.w * p.s * k) / 2; const hh = (PLATE.h * p.sy * k) / 2;
            const lw = 7;
            const edgeOn = shown && t >= BIG - 40 && (mode !== 'sink' || t < SINK);
            [[0, -hh, hw * 2 + lw, lw], [0, hh, hw * 2 + lw, lw], [-hw, 0, lw, hh * 2 + lw], [hw, 0, lw, hh * 2 + lw]].forEach(([dx, dy, sx, sy], i) => {
              place(edge[i], p.x + dx, p.y + dy, { sx, sy });
              fade(edge[i], edgeOn ? 1 : 0);
            });
          },
        };
      },
    },
  ],
});
